import { useMemo, useState, type ReactNode } from 'react';
import { DeleteButton, ErrorBanner, Field, Modal } from '../../components/ui';
import { api } from '../../lib/api';
import type { Taxonomy } from './audit';

/**
 * Editing the rules.
 *
 * ANCHORED ON CATEGORY, because that is what the rules are. Three of the four
 * are "which of these may this category use", so the editor picks a category
 * and ticks them — the shape somebody thinks in, and the shape the source
 * spreadsheet is laid out in.
 *
 * The fourth is different and is edited somewhere else: a sub-issue belongs to
 * an ISSUE TYPE, not to a category, so it hangs off the issue type and follows
 * it into whatever categories that issue type is used by. Putting sub-issues
 * on the category screen would quietly imply they can differ per category.
 *
 * NOTHING SAVES UNTIL SAVE. The whole taxonomy is one document, and a tick
 * that wrote through immediately would leave the rules half-changed if the
 * page were closed mid-edit — with no way to tell which half. The draft lives
 * here and goes over in one PUT.
 */

type Row = { name: string; categories: string[] };

const EDITED = ' (edited in app)';

/** Mark the rules as no longer matching their source sheet — idempotently. */
const markEdited = (name: string | null): string =>
  (name ?? 'Imported rules').endsWith(EDITED) ? name! : `${name ?? 'Imported rules'}${EDITED}`;

export function RulesEditor({
  taxonomy,
  sourceName,
  onClose,
  onSaved,
}: {
  taxonomy: Taxonomy;
  sourceName: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<Taxonomy>(() => structuredClone(taxonomy));
  const [cat, setCat] = useState(taxonomy.categories[0] ?? '');
  const [tab, setTab] = useState<'category' | 'issues'>('category');
  /* Which issue type the sub-issue tab opens on, when it is opened from an
     issue type's "Edit sub-issues" on the category tab. */
  const [subIssuesFor, setSubIssuesFor] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(taxonomy),
    [draft, taxonomy],
  );

  const save = () => {
    setSaving(true);
    setError(null);
    void api
      .put('/tickets/taxonomy', {
        doc: draft,
        /* The rules no longer match the sheet once they are edited here, and
           the audit page prints this name as "checked against". Saying it is
           still the spreadsheet would be a lie the moment a tick changes.

           Marked once, not once per save: appending unconditionally turns a
           name into "... (edited in app) (edited in app)" by the third edit. */
        sourceName: markEdited(sourceName),
      })
      .then(() => {
        onSaved();
        onClose();
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not save the rules'))
      .finally(() => setSaving(false));
  };

  /** Toggle one row's membership of the category being edited. */
  const toggle = (key: 'statuses' | 'workTypes' | 'issueTypes', name: string) => {
    setDraft((d) => {
      const rows = (d[key] as Row[]).map((r) =>
        r.name !== name
          ? r
          : {
              ...r,
              categories: r.categories.includes(cat)
                ? r.categories.filter((c) => c !== cat)
                : [...r.categories, cat],
            },
      );
      return { ...d, [key]: rows };
    });
  };

  const addRow = (key: 'statuses' | 'workTypes' | 'issueTypes', name: string) => {
    const clean = name.trim();
    if (!clean) return;
    setDraft((d) => {
      const rows = d[key] as Row[];
      if (rows.some((r) => r.name.toLowerCase() === clean.toLowerCase())) return d;
      /* Created already ticked for the category being edited — adding one from
         this screen means "this category uses it", and making them tick it
         again would be a second step with one sensible answer. */
      const made =
        key === 'issueTypes'
          ? { name: clean, categories: [cat], subIssues: [] }
          : { name: clean, categories: [cat] };
      return { ...d, [key]: [...rows, made].sort((a, b) => a.name.localeCompare(b.name)) };
    });
  };

  const removeRow = (key: 'statuses' | 'workTypes' | 'issueTypes', name: string) => {
    setDraft((d) => ({ ...d, [key]: (d[key] as Row[]).filter((r) => r.name !== name) }));
  };

  return (
    <Modal
      title="Edit the rules"
      onClose={onClose}
      footer={
        <>
          <span className="xs muted grow">
            {dirty ? 'Unsaved changes.' : 'No changes yet.'}
          </span>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!dirty || saving}
            onClick={save}
          >
            {saving ? 'Saving…' : 'Save rules'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <div className="gwc-toggle mb-4">
        <button className={tab === 'category' ? 'on-yes' : ''} onClick={() => setTab('category')}>
          By category
        </button>
        <button className={tab === 'issues' ? 'on-yes' : ''} onClick={() => setTab('issues')}>
          Issue types &amp; sub-issues
        </button>
      </div>

      {tab === 'category' ? (
        <>
          <Field
            label="Ticket category"
            hint="The anchor. Everything ticked below is what a ticket in this category may use."
          >
            <select className="select" value={cat} onChange={(e) => setCat(e.target.value)}>
              {draft.categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>

          <div className="stack mt-4" style={{ gap: 'var(--space-5)' }}>
            <TickList
              title="Statuses"
              rows={draft.statuses}
              cat={cat}
              onToggle={(n) => toggle('statuses', n)}
              onAdd={(n) => addRow('statuses', n)}
              onRemove={(n) => removeRow('statuses', n)}
            />
            <TickList
              title="Work types"
              note="Independent of the statuses — the two lists do not relate."
              rows={draft.workTypes}
              cat={cat}
              onToggle={(n) => toggle('workTypes', n)}
              onAdd={(n) => addRow('workTypes', n)}
              onRemove={(n) => removeRow('workTypes', n)}
            />
            <TickList
              title="Issue types"
              note="Each one's sub-issues are listed under it. A sub-issue belongs to the issue type rather than to this category, so it follows the issue type into every category that uses it — edit them with Edit sub-issues."
              rows={draft.issueTypes}
              cat={cat}
              onToggle={(n) => toggle('issueTypes', n)}
              onAdd={(n) => addRow('issueTypes', n)}
              onRemove={(n) => removeRow('issueTypes', n)}
              wide
              detail={(row) => {
                const subs = draft.issueTypes.find((i) => i.name === row.name)?.subIssues ?? [];
                return (
                  <div className="rule-subs">
                    {subs.length === 0 ? (
                      <span className="xs subtle">No sub-issues — one filed under this cannot be checked.</span>
                    ) : (
                      <span className="xs muted">
                        {subs.length} sub-issue{subs.length === 1 ? '' : 's'}: {subs.join(' · ')}
                      </span>
                    )}
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => {
                        setSubIssuesFor(row.name);
                        setTab('issues');
                      }}
                    >
                      Edit sub-issues
                    </button>
                  </div>
                );
              }}
            />
          </div>
        </>
      ) : (
        <SubIssueEditor
          key={subIssuesFor ?? ''}
          draft={draft}
          setDraft={setDraft}
          initial={subIssuesFor}
        />
      )}
    </Modal>
  );
}

/* =========================================================================
   One tickable list
   ========================================================================= */

function TickList({
  title,
  note,
  rows,
  cat,
  onToggle,
  onAdd,
  onRemove,
  detail,
  wide = false,
}: {
  title: string;
  note?: string;
  rows: Row[];
  cat: string;
  onToggle: (name: string) => void;
  onAdd: (name: string) => void;
  onRemove: (name: string) => void;
  /** Shown under a row — the issue types use it for their sub-issues. Kept
   *  outside the row's label, so clicking in it does not tick the box. */
  detail?: (row: Row) => ReactNode;
  /** One row to a line, for rows that carry a detail. */
  wide?: boolean;
}) {
  const [adding, setAdding] = useState('');
  const on = rows.filter((r) => r.categories.includes(cat)).length;

  return (
    <div>
      <div className="row-between wrap">
        <span className="eyebrow">
          {title} — {on} of {rows.length} allowed here
        </span>
      </div>
      {note && <p className="xs muted">{note}</p>}
      {/* A row with no category at all is the state that makes a value
          unverifiable rather than wrong, so it is worth seeing while editing. */}
      <div className={`rule-ticks mt-2${wide ? ' is-wide' : ''}`}>
        {rows.map((r) => {
          const checked = r.categories.includes(cat);
          const orphan = r.categories.length === 0;
          return (
            <div key={r.name}>
            <label className={`rule-tick${checked ? ' is-on' : ''}`}>
              <input type="checkbox" checked={checked} onChange={() => onToggle(r.name)} />
              <span className="grow">{r.name}</span>
              {orphan && (
                <span className="xs subtle" title="Used by no category, so it can never be checked">
                  unused
                </span>
              )}
              <DeleteButton
                what={`"${r.name}" from every category`}
                label="×"
                onConfirm={() => onRemove(r.name)}
              />
            </label>
            {detail?.(r)}
            </div>
          );
        })}
      </div>
      <div className="row mt-2">
        <input
          className="input"
          placeholder={`Add a ${title.toLowerCase().replace(/s$/, '')}…`}
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onAdd(adding);
              setAdding('');
            }
          }}
        />
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => {
            onAdd(adding);
            setAdding('');
          }}
        >
          Add
        </button>
      </div>
    </div>
  );
}

/* =========================================================================
   Sub-issues, which hang off the issue type
   ========================================================================= */

function SubIssueEditor({
  draft,
  setDraft,
  initial,
}: {
  draft: Taxonomy;
  setDraft: (fn: (d: Taxonomy) => Taxonomy) => void;
  /** The issue type to open on — set when arriving from the category tab. */
  initial: string | null;
}) {
  const [pick, setPick] = useState(initial ?? draft.issueTypes[0]?.name ?? '');
  const [adding, setAdding] = useState('');
  const issue = draft.issueTypes.find((i) => i.name === pick) ?? null;

  const setSubs = (fn: (subs: string[]) => string[]) =>
    setDraft((d) => ({
      ...d,
      issueTypes: d.issueTypes.map((i) => (i.name === pick ? { ...i, subIssues: fn(i.subIssues) } : i)),
    }));

  /* A sub-issue belongs to exactly one issue type in the source, and the
     checker relies on that: moving one is a remove plus an add, never a
     second membership. */
  const usedElsewhere = (name: string) =>
    draft.issueTypes.find((i) => i.name !== pick && i.subIssues.some((s) => s === name))?.name ?? null;

  return (
    <>
      <Field
        label="Issue type"
        hint="A sub-issue belongs to its issue type and follows it into every category that uses it."
      >
        <select className="select" value={pick} onChange={(e) => setPick(e.target.value)}>
          {draft.issueTypes.map((i) => (
            <option key={i.name} value={i.name}>
              {i.name} ({i.subIssues.length})
            </option>
          ))}
        </select>
      </Field>

      {issue && (
        <div className="mt-4">
          <span className="eyebrow">Sub-issues — {issue.subIssues.length}</span>
          {issue.subIssues.length === 0 && (
            <p className="xs muted">
              None listed. A sub-issue on a ticket filed under this issue type cannot be checked
              until there are some.
            </p>
          )}
          <div className="rule-ticks mt-2">
            {issue.subIssues.map((s) => (
              <div key={s} className="rule-tick is-on">
                <span className="grow">{s}</span>
                <DeleteButton
                  what={`the sub-issue "${s}"`}
                  label="×"
                  onConfirm={() => setSubs((subs) => subs.filter((x) => x !== s))}
                />
              </div>
            ))}
          </div>

          <div className="row mt-2">
            <input
              className="input"
              placeholder="Add a sub-issue…"
              value={adding}
              onChange={(e) => setAdding(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                const clean = adding.trim();
                if (!clean) return;
                setSubs((subs) => (subs.includes(clean) ? subs : [...subs, clean].sort()));
                setAdding('');
              }}
            >
              Add
            </button>
          </div>

          {adding.trim() && usedElsewhere(adding.trim()) && (
            <p className="risk-note xs mt-2">
              <strong>Already used. </strong>
              "{adding.trim()}" is a sub-issue of {usedElsewhere(adding.trim())}. A sub-issue belongs
              to one issue type — remove it there first if it should move.
            </p>
          )}
        </div>
      )}
    </>
  );
}
